import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "@/lib/data";
import { EASE, btnPrimary, ArrowIcon } from "@/components/primitives";

function LogoLockup() {
  return (
    <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
      <img src="/assets/timepe-logo.png" alt="TimePe logo" className="h-11 w-auto object-contain" />
      <span className="hidden sm:block">
        <span className="block font-mono text-[10px] uppercase tracking-[0.24em] text-mute">A platform by</span>
        <span className="block font-display text-[15px] font-semibold tracking-tight text-mist">Avyagrah Tech Innovations</span>
      </span>
    </Link>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <header
      data-testid="main-nav"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
        scrolled ? "border-b border-line" : ""
      }`}
    >
      {scrolled && (
        <div className="absolute inset-0 bg-ink/85 backdrop-blur-xl" aria-hidden="true" />
      )}
      <div className="relative mx-auto flex h-[76px] max-w-[1440px] items-center justify-between px-5 lg:px-8">
        <LogoLockup />
        <nav className="hidden items-center gap-5 xl:gap-6 lg:flex" aria-label="Primary">
          {NAV_LINKS.slice(1).map((link) => (
            <NavLink
              key={link.path}
              to={link.path}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-[12.5px] font-medium tracking-wide transition-colors duration-300 ${
                  isActive ? "text-electric" : "text-mute hover:text-mist"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <Link to="/partners" data-testid="nav-cta-partner" className={`${btnPrimary} hidden !px-5 !py-2.5 text-[13px] md:inline-flex`}>
            Partner With Us
            <ArrowIcon />
          </Link>
          <button
            data-testid="mobile-menu-button"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            onClick={() => setOpen(!open)}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-line text-mist transition-colors hover:border-electric/50 lg:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 top-[76px] z-40 overflow-y-auto bg-ink/98 backdrop-blur-xl lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <nav className="flex flex-col px-6 py-8" aria-label="Mobile">
              {NAV_LINKS.map((link, i) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 + i * 0.04, duration: 0.5, ease: EASE }}
                >
                  <NavLink
                    to={link.path}
                    data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) =>
                      `block border-b border-line py-4 font-display text-2xl font-semibold tracking-tight ${
                        isActive ? "text-electric" : "text-mist"
                      }`
                    }
                  >
                    {link.label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.div
                className="mt-8 flex flex-col gap-3"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.55, duration: 0.5, ease: EASE }}
              >
                <Link to="/partners" data-testid="mobile-cta-partner" className={`${btnPrimary} justify-center`}>
                  Partner With Us <ArrowIcon />
                </Link>
                <Link to="/contact" data-testid="mobile-cta-contact" className="inline-flex items-center justify-center gap-2 rounded-full border border-line px-6 py-3 text-sm font-semibold text-mist">
                  Contact
                </Link>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
